import { supabase } from "@/integrations/supabase/client";

/**
 * Resolves which ad configuration should be shown for a given article
 * Priority order: specific_article > category > all_articles
 * Within each level, higher priority number wins
 */
export async function resolveArticleAdConfig(
  articleId: string,
  categoryId?: string
): Promise<string | null> {
  try {
    // Build OR conditions - always include all_articles
    const conditions: string[] = ['target_type.eq.all_articles'];

    // Add specific article targeting if articleId provided
    if (articleId) {
      conditions.push(`article_id.eq.${articleId}`);
    }

    // Add category targeting if categoryId provided  
    if (categoryId) {
      conditions.push(`category_id.eq.${categoryId}`);
    }

    const { data: targetings, error } = await supabase
      .from('article_ad_targeting')
      .select(`
        config_id,
        target_type,
        priority,
        article_id,
        category_id,
        article_ad_configs!inner(is_active)
      `)
      .or(conditions.join(','))
      .eq('article_ad_configs.is_active', true)
      .order('priority', { ascending: false });

    if (error) throw error;
    if (!targetings || targetings.length === 0) return null;

    // Filter and prioritize - only match relevant targeting rules
    const specificArticle = targetings.filter(t => 
      t.target_type === 'specific_article' && t.article_id === articleId
    );
    const category = targetings.filter(t => 
      t.target_type === 'category' && t.category_id === categoryId
    );
    const allArticles = targetings.filter(t => 
      t.target_type === 'all_articles'
    );

    // Return highest priority config from most specific match
    if (specificArticle.length > 0) return specificArticle[0].config_id;
    if (category.length > 0) return category[0].config_id;
    if (allArticles.length > 0) return allArticles[0].config_id;

    return null;
  } catch (error) {
    console.error('Error resolving article ad config:', error);
    return null;
  }
}
